import { PageLayout } from "@/components/layout/PageLayout";
import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { 
  Code, Key, Zap, Shield, Copy, CheckCircle, ExternalLink,
  ArrowRight, Server, Database, Activity
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const pricingTiers = [
  {
    name: "Starter",
    price: 49,
    calls: "10,000",
    features: ["Basic endpoints", "Standard support", "1 API key"],
    popular: false,
  },
  {
    name: "Pro",
    price: 149,
    calls: "100,000",
    features: ["All endpoints", "Priority support", "5 API keys", "Webhooks"],
    popular: true,
  },
  {
    name: "Enterprise",
    price: 499,
    calls: "Unlimited",
    features: ["All endpoints", "Dedicated support", "Unlimited keys", "Webhooks", "Custom integration", "SLA"],
    popular: false,
  },
];

const endpoints = [
  { method: "GET", path: "/api/v1/tokens", description: "List all tokens with filters" },
  { method: "GET", path: "/api/v1/tokens/{id}", description: "Get token details" },
  { method: "GET", path: "/api/v1/tokens/{id}/price", description: "Get real-time price" },
  { method: "GET", path: "/api/v1/tokens/{id}/trades", description: "Get recent trades" },
  { method: "GET", path: "/api/v1/tokens/{id}/holders", description: "Get holder distribution" },
  { method: "POST", path: "/api/v1/trades/quote", description: "Get trade quote" },
  { method: "POST", path: "/api/v1/trades/execute", description: "Execute trade (requires wallet)" },
  { method: "GET", path: "/api/v1/wallet/{address}", description: "Get wallet holdings" },
  { method: "GET", path: "/api/v1/trending", description: "Get trending tokens" },
  { method: "GET", path: "/api/v1/new", description: "Get newly launched tokens" },
  { method: "WS", path: "/api/v1/stream/trades", description: "Real-time trade stream" },
  { method: "WS", path: "/api/v1/stream/prices", description: "Real-time price updates" },
];

const codeExample = `// Example: Fetch token price
const response = await fetch('https://api.themoon.run/v1/tokens/MDOGE/price', {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});

const data = await response.json();
console.log(data);
// { price: 0.00001234, change_24h: 12.5, volume_24h: 45000 }`;

const webhookExample = `// Webhook payload example (sent to your endpoint)
{
  "event": "trade.executed",
  "data": {
    "token": "MDOGE",
    "type": "buy",
    "amount_sol": 1.5,
    "amount_tokens": 150000,
    "price": 0.00001,
    "wallet": "7xKXt...9hG2",
    "tx_hash": "5abc..."
  },
  "timestamp": "2024-01-15T10:30:00Z"
}`;

export default function API() {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast({ title: "Copied!", description: "Code copied to clipboard" });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Helmet>
        <title>API Access - TheMoonRun</title>
        <meta name="description" content="Integrate TheMoonRun into your applications. Real-time data, trading, and webhooks." />
      </Helmet>
      
      <PageLayout>
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Code className="w-7 h-7 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">API Access</h1>
            <p className="text-muted-foreground">Build on TheMoonRun • Real-time data & trading</p>
          </div>
        </div>

              {/* Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className="p-4 rounded-xl bg-card border border-border flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Server className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-mono font-bold">99.9%</p>
                    <p className="text-xs text-muted-foreground">Uptime SLA</p>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-card border border-border flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-success" />
                  </div>
                  <div>
                    <p className="text-2xl font-mono font-bold">&lt;50ms</p>
                    <p className="text-xs text-muted-foreground">Avg Latency</p>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-card border border-border flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-warning" />
                  </div>
                  <div>
                    <p className="text-2xl font-mono font-bold">1M+</p>
                    <p className="text-xs text-muted-foreground">Daily Requests</p>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="mb-12">
                <h2 className="text-xl font-semibold mb-6">Pricing Plans</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {pricingTiers.map((tier) => (
                    <div 
                      key={tier.name}
                      className={cn(
                        "p-6 rounded-xl border-2 transition-all",
                        tier.popular 
                          ? "bg-primary/5 border-primary" 
                          : "bg-card border-border"
                      )}
                    >
                      {tier.popular && (
                        <Badge className="mb-4">Most Popular</Badge>
                      )}
                      <h3 className="text-xl font-semibold">{tier.name}</h3>
                      <div className="mt-4 mb-6">
                        <span className="text-4xl font-mono font-bold">${tier.price}</span>
                        <span className="text-muted-foreground">/month</span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        {tier.calls} API calls/month
                      </p>
                      <ul className="space-y-2 mb-6">
                        {tier.features.map((feature) => (
                          <li key={feature} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-success" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <Button 
                        className="w-full" 
                        variant={tier.popular ? "default" : "outline"}
                      >
                        Get Started
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <Tabs defaultValue="endpoints">
                <TabsList className="mb-6">
                  <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
                  <TabsTrigger value="examples">Code Examples</TabsTrigger>
                  <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
                  <TabsTrigger value="keys">Your API Keys</TabsTrigger>
                </TabsList>

                <TabsContent value="endpoints">
                  <div className="rounded-xl bg-card border border-border overflow-hidden">
                    <div className="p-4 border-b border-border">
                      <h3 className="font-semibold">Available Endpoints</h3>
                      <p className="text-sm text-muted-foreground">Base URL: https://api.themoon.run</p>
                    </div>
                    <div className="divide-y divide-border">
                      {endpoints.map((endpoint) => (
                        <div key={endpoint.path} className="p-4 flex items-center gap-4 hover:bg-secondary/30">
                          <Badge variant={
                            endpoint.method === "GET" ? "secondary" :
                            endpoint.method === "POST" ? "default" : "outline"
                          } className={cn(
                            "w-14 justify-center font-mono text-xs",
                            endpoint.method === "WS" && "bg-warning/20 text-warning border-warning/30"
                          )}>
                            {endpoint.method}
                          </Badge>
                          <code className="font-mono text-sm text-primary flex-1">{endpoint.path}</code>
                          <span className="text-sm text-muted-foreground">{endpoint.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="examples">
                  <div className="rounded-xl bg-card border border-border overflow-hidden">
                    <div className="p-4 border-b border-border flex items-center justify-between">
                      <h3 className="font-semibold">JavaScript / TypeScript</h3>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => copyCode(codeExample)}
                      >
                        {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    <pre className="p-4 overflow-x-auto text-sm">
                      <code className="text-muted-foreground">{codeExample}</code>
                    </pre>
                  </div>
                </TabsContent>

                <TabsContent value="webhooks">
                  <div className="space-y-6">
                    <div className="rounded-xl bg-card border border-border p-6">
                      <h3 className="font-semibold mb-4">Configure Webhook</h3>
                      <div className="flex gap-4">
                        <Input placeholder="https://your-server.com/webhook" className="flex-1" />
                        <Button>Save Endpoint</Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        We'll send POST requests to this URL when events occur
                      </p>
                    </div>

                    <div className="rounded-xl bg-card border border-border overflow-hidden">
                      <div className="p-4 border-b border-border">
                        <h3 className="font-semibold">Webhook Payload Example</h3>
                      </div>
                      <pre className="p-4 overflow-x-auto text-sm">
                        <code className="text-muted-foreground">{webhookExample}</code>
                      </pre>
                    </div>

                    <div className="rounded-xl bg-card border border-border p-6">
                      <h3 className="font-semibold mb-4">Available Events</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[
                          "trade.executed", "token.created", "token.locked", "token.unlocked",
                          "price.alert", "whale.detected", "vote.cast", "user.joined"
                        ].map((event) => (
                          <div key={event} className="flex items-center gap-2 text-sm">
                            <Zap className="w-4 h-4 text-primary" />
                            <code className="font-mono">{event}</code>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="keys">
                  <div className="rounded-xl bg-card border border-border p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="font-semibold">Your API Keys</h3>
                        <p className="text-sm text-muted-foreground">Manage your API access</p>
                      </div>
                      <Button>
                        <Key className="w-4 h-4 mr-2" />
                        Generate New Key
                      </Button>
                    </div>

                    <div className="p-8 rounded-lg border border-dashed border-border text-center">
                      <Shield className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                      <p className="text-muted-foreground mb-4">Connect your wallet to manage API keys</p>
                      <Button variant="outline">Connect Wallet</Button>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              {/* CTA */}
              <div className="mt-12 p-8 rounded-2xl bg-gradient-to-r from-primary/20 to-accent/10 border border-primary/20 text-center">
                <h2 className="text-2xl font-display font-bold mb-2">Ready to build?</h2>
                <p className="text-muted-foreground mb-6">
                  Get started with our API in minutes. Full documentation available.
                </p>
                <div className="flex items-center justify-center gap-4">
                  <Button>
                    Get API Key
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button variant="outline">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View Docs
                  </Button>
                </div>
              </div>
      </PageLayout>
    </>
  );
}
